<template>
  <div class="sms-page">
    <audio
      ref="player"
      class="voice-player"
      playsinline
      webkit-playsinline
      preload="auto"
    ></audio>
    <div class="title-bar">
      <span>Voice Messages</span>
      <div class="title-buttons">
        <button
          type="button"
          class="title-button"
          title="Back"
          @click="$router.go(-1)"
        >
          <i class="fas fa-arrow-left"></i>
        </button>
        <button
          type="button"
          class="title-button"
          title="Refresh"
          :disabled="refreshing"
          @click="refreshInbox"
        >
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': refreshing }"></i>
        </button>
        <button
          v-if="isEmbedded"
          type="button"
          class="title-button"
          title="Close"
          @click="sendClose"
        >
          <i class="fas fa-close"></i>
        </button>
        <button
          type="button"
          class="title-button"
          title="Home"
          @click="$router.push('/mhome')"
        >
          <i class="fas fa-home"></i>
        </button>
      </div>
    </div>

    <div class="sms-hint">voicemail, chat, and staff recordings.</div>

    <div class="sms-list-filter">
      <div class="sms-list-filter-wrap">
        <input
          v-model="listFilterInput"
          class="sms-list-filter-input"
          type="text"
          placeholder="Filter by name, number, or transcript"
          @input="onListFilterInput"
        />
        <button
          v-if="listFilterInput || listQuery"
          type="button"
          class="sms-list-filter-clear"
          title="Clear filter"
          @click="clearListFilter"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>

    <div class="sms-list">
      <div v-if="loading" class="sms-status">Loading…</div>
      <div v-else-if="loadError" class="sms-status sms-error">{{ loadError }}</div>
      <div v-else-if="!inbox.length" class="sms-status">No incoming calls.</div>
      <div v-else-if="!visibleInbox.length" class="sms-status">No matches.</div>
      <template v-else>
        <div
          v-for="group in groupedInbox"
          :key="group.key"
          class="voice-group"
          :class="{ 'voice-group-older': group.key === 'older' }"
        >
          <button
            type="button"
            class="voice-group-header"
            :aria-expanded="!collapsed[group.key]"
            @click="toggleGroup(group.key)"
          >
            <i
              :class="
                collapsed[group.key]
                  ? 'fas fa-chevron-right'
                  : 'fas fa-chevron-down'
              "
            ></i>
            <span>{{ group.label }}</span>
            <span class="voice-group-count">{{ group.items.length }}</span>
          </button>
          <div v-show="!collapsed[group.key]">
            <div
              v-for="msg in group.items"
              :key="msg.id"
              class="sms-item"
              :data-push-id="msg.id"
              :class="{
                'sms-item-selected': selectedId === msg.id,
                'sms-item-unlistened': !msg.listenedOn,
                'sms-item-push-highlight': highlightId === msg.id,
              }"
            >
              <div class="sms-stack">
                <div class="sms-item-top">
                  <span class="voice-from-row">
                    <span class="sms-from">
                      <span
                        class="voice-kind"
                        :class="{
                          'voice-kind-office': sourceLabel(msg) === 'office',
                        }"
                        >{{ sourceLabel(msg) }}</span
                      >
                      <span>{{ displayTitle(msg) }}</span>
                    </span>
                    <button
                      v-if="unknownCaller(msg)"
                      type="button"
                      class="sms-create-contact"
                      @click.stop="beginExternalName(msg)"
                    >
                      create contact
                    </button>
                    <button
                      v-else-if="externalCaller(msg)"
                      type="button"
                      class="sms-create-contact"
                      @click.stop="openExternalEdit(msg)"
                    >
                      edit
                    </button>
                  </span>
                  <span class="sms-ago">{{
                    stamp(msg.createdOn, transcriptId === msg.id)
                  }}</span>
                </div>
                <div class="sms-bubble">
                  <div class="voice-actions">
                    <button
                      type="button"
                      class="voice-action"
                      :title="playingId === msg.id ? 'Pause' : 'Play audio'"
                      @click.stop="togglePlay(msg)"
                    >
                      <i
                        :class="
                          playingId === msg.id && !playPaused
                            ? 'fas fa-pause'
                            : 'fas fa-play'
                        "
                      ></i>
                      <span>{{
                        playingId === msg.id && !playPaused ? 'Pause' : 'Play'
                      }}</span>
                    </button>
                    <button
                      type="button"
                      class="voice-action"
                      title="View transcript"
                      @click.stop="toggleTranscript(msg)"
                    >
                      <i class="fas fa-align-left"></i>
                      <span>Transcript</span>
                    </button>
                    <button
                      type="button"
                      class="voice-action"
                      title="Delete"
                      :disabled="deletingId === msg.id"
                      @click.stop="deleteMessage(msg)"
                    >
                      <i
                        :class="
                          deletingId === msg.id
                            ? 'fas fa-spinner fa-spin'
                            : 'fas fa-trash'
                        "
                      ></i>
                      <span>Delete</span>
                    </button>
                  </div>
                  <div v-if="playError && selectedId === msg.id" class="sms-error">
                    {{ playError }}
                  </div>
                  <div
                    v-if="playingId === msg.id"
                    class="voice-scrub"
                    @click.stop
                  >
                    <span class="voice-scrub-time">{{
                      formatClock(playCurrent)
                    }}</span>
                    <input
                      class="voice-scrub-bar"
                      type="range"
                      min="0"
                      step="0.05"
                      :max="playDuration || 0"
                      :value="playCurrent"
                      :aria-label="'Playback position'"
                      @input="onScrubInput"
                      @change="onScrubCommit"
                    />
                    <span class="voice-scrub-time">{{
                      formatClock(playDuration)
                    }}</span>
                  </div>
                  <div
                    v-if="transcriptId === msg.id"
                    class="sms-body sms-body-expanded"
                  >
                    {{ transcriptText(msg) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
    <div
      v-if="editingId"
      class="voice-contact-overlay"
      @click.self="cancelExternalName"
    >
      <div
        class="voice-contact-dialog"
        role="dialog"
        aria-label="External contact"
      >
        <p>Name for {{ editingPhone }}</p>
        <input
          ref="externalNameInput"
          v-model="editingName"
          class="voice-name-input"
          type="text"
          maxlength="128"
          placeholder="Name"
          @keydown.enter.prevent="saveExternalName"
          @keydown.esc.prevent="cancelExternalName"
        />
        <span v-if="nameError" class="sms-error">{{ nameError }}</span>
        <div class="voice-contact-actions">
          <button
            type="button"
            class="voice-name-save"
            :disabled="savingName || !editingName.trim()"
            @click="saveExternalName"
          >
            {{ savingName ? 'Saving…' : 'Save' }}
          </button>
          <button
            type="button"
            class="voice-contact-cancel"
            :disabled="savingName"
            @click="cancelExternalName"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
    <div
      v-if="externalEdit"
      class="voice-contact-overlay"
      @click.self="closeExternalEdit"
    >
      <div
        class="voice-contact-dialog"
        role="dialog"
        aria-label="Edit external contact"
      >
        <p>Edit contact</p>
        <label class="voice-edit-field">
          Phone
          <input
            ref="externalEditPhoneInput"
            v-model="externalEditPhone"
            class="voice-name-input"
            type="tel"
            maxlength="20"
            :disabled="externalEditSaving"
            @keydown.enter.prevent="saveExternalEdit"
            @keydown.esc.prevent="closeExternalEdit"
          />
        </label>
        <label class="voice-edit-field">
          Name
          <input
            v-model="externalEditName"
            class="voice-name-input"
            type="text"
            maxlength="128"
            :disabled="externalEditSaving"
            @keydown.enter.prevent="saveExternalEdit"
            @keydown.esc.prevent="closeExternalEdit"
          />
        </label>
        <label class="voice-edit-optin">
          <input
            v-model="externalEditOptIn"
            type="checkbox"
            :disabled="externalEditSaving || !externalEditReady"
          />
          Opt in to text alerts
        </label>
        <span v-if="externalEditError" class="sms-error">{{
          externalEditError
        }}</span>
        <div class="voice-contact-actions">
          <button
            type="button"
            class="voice-name-save"
            :disabled="!externalEditCanSave"
            @click="saveExternalEdit"
          >
            {{ externalEditSaving ? 'Saving…' : 'Save' }}
          </button>
          <button
            type="button"
            class="voice-contact-cancel"
            :disabled="externalEditSaving"
            @click="closeExternalEdit"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axiosConfig';
import eventBus from './eventBus';
import {
  formatSmsInboxStamp,
  formatSmsPhone,
  parseSmsSearchPhone,
  externalContactEditError,
} from '@/utils/smsInbox';
import {
  voiceMessagesForRole,
  filterVoiceMessagesSearch,
  voiceMessageSourceLabel,
  voiceMessageDisplayTitle,
  voiceMessageSource,
  voiceMessageTranscriptText,
  voiceMessagesIsEmbedded,
  groupVoiceMessagesByDate,
  voiceMessageGroupCollapsedByDefault,
  formatVoicePlaybackClock,
  clampVoiceSeekTime,
  dropVoiceMessageById,
  sniffAudioMimeFromBytes,
  voicePlaybackParams,
  unknownVoicemailCaller,
  externalVoicemailCaller,
  applyExternalNameToVoiceInbox,
  applyExternalEditToVoiceInbox,
} from '@/utils/voiceMessages';
import { pushHighlightPlan } from '@/utils/pushOpenTarget';

export default {
  name: 'MVoiceMessages',
  data() {
    return {
      inbox: [],
      loading: false,
      loadError: '',
      refreshing: false,
      selectedId: null,
      transcriptId: null,
      deletingId: null,
      playingId: null,
      playPaused: false,
      playCurrent: 0,
      playDuration: 0,
      playSeeking: false,
      playError: '',
      audioEl: null,
      objectUrl: null,
      onAudioTime: null,
      onAudioMeta: null,
      onAudioEnded: null,
      listFilterInput: '',
      listQuery: '',
      listFilterTimer: null,
      highlightId: null,
      editingId: null,
      editingDigits: '',
      editingPhone: '',
      editingName: '',
      savingName: false,
      nameError: '',
      externalEdit: null,
      externalEditPhone: '',
      externalEditName: '',
      externalEditOptIn: false,
      externalEditError: '',
      externalEditReady: false,
      externalEditSaving: false,
      userRole: null,
      clock: Date.now(),
      clockTimer: null,
      collapsed: {
        today: voiceMessageGroupCollapsedByDefault('today'),
        yesterday: voiceMessageGroupCollapsedByDefault('yesterday'),
        week: voiceMessageGroupCollapsedByDefault('week'),
        older: voiceMessageGroupCollapsedByDefault('older'),
      },
    };
  },
  computed: {
    isEmbedded() {
      return voiceMessagesIsEmbedded(this.$route && this.$route.path);
    },
    visibleInbox() {
      return filterVoiceMessagesSearch(this.inbox, this.listQuery);
    },
    groupedInbox() {
      void this.clock;
      return groupVoiceMessagesByDate(this.visibleInbox, new Date(this.clock));
    },
    externalEditCanSave() {
      return (
        !!this.externalEdit &&
        this.externalEditReady &&
        !this.externalEditSaving &&
        !!String(this.externalEditName || '').trim() &&
        !!parseSmsSearchPhone(this.externalEditPhone)
      );
    },
  },
  watch: {
    inbox: 'tryApplyPushHighlight',
    '$route.query.id': 'tryApplyPushHighlight',
  },
  mounted() {
    this.fetchInbox();
    this.clockTimer = setInterval(() => {
      this.clock = Date.now();
    }, 30000);
  },
  beforeUnmount() {
    if (this._highlightTimer) clearTimeout(this._highlightTimer);
    if (this.clockTimer) clearInterval(this.clockTimer);
    if (this.listFilterTimer) clearTimeout(this.listFilterTimer);
    this.stopPlayback();
  },
  methods: {
    sendClose() {
      eventBus.emit('requestCloseVoiceMessages');
    },
    toggleGroup(key) {
      this.collapsed[key] = !this.collapsed[key];
    },
    stamp(date, expanded) {
      void this.clock;
      return formatSmsInboxStamp(date, expanded);
    },
    transcriptText(msg) {
      return voiceMessageTranscriptText(msg);
    },
    formatClock(seconds) {
      return formatVoicePlaybackClock(seconds);
    },
    sourceLabel(msg) {
      return voiceMessageSourceLabel(voiceMessageSource(msg));
    },
    displayTitle(msg) {
      return voiceMessageDisplayTitle(msg);
    },
    bindAudioEl() {
      const audio = this.$refs.player;
      if (!audio) return null;
      this.audioEl = audio;
      audio.volume = 1;
      audio.muted = false;
      if (!this.onAudioTime) {
        this.onAudioTime = () => {
          if (this.playSeeking) return;
          this.playCurrent = this.audioEl.currentTime || 0;
          const dur = this.audioEl.duration;
          if (Number.isFinite(dur) && dur > 0) this.playDuration = dur;
        };
        this.onAudioMeta = () => {
          const dur = this.audioEl.duration;
          this.playDuration = Number.isFinite(dur) && dur > 0 ? dur : 0;
        };
        this.onAudioEnded = () => {
          this.playingId = null;
          this.playPaused = false;
          this.playCurrent = 0;
        };
        audio.addEventListener('timeupdate', this.onAudioTime);
        audio.addEventListener('loadedmetadata', this.onAudioMeta);
        audio.addEventListener('ended', this.onAudioEnded);
      }
      return audio;
    },
    onScrubInput(event) {
      this.playSeeking = true;
      const t = clampVoiceSeekTime(event.target.value, this.playDuration);
      this.playCurrent = t;
      if (this.audioEl) this.audioEl.currentTime = t;
    },
    onScrubCommit(event) {
      const t = clampVoiceSeekTime(event.target.value, this.playDuration);
      this.playCurrent = t;
      if (this.audioEl) this.audioEl.currentTime = t;
      this.playSeeking = false;
    },
    onListFilterInput() {
      if (this.listFilterTimer) clearTimeout(this.listFilterTimer);
      this.listFilterTimer = setTimeout(() => {
        this.listQuery = this.listFilterInput;
      }, 220);
    },
    clearListFilter() {
      this.listFilterInput = '';
      this.listQuery = '';
    },
    unknownCaller(msg) {
      return unknownVoicemailCaller(msg && msg.title);
    },
    externalCaller(msg) {
      return externalVoicemailCaller(msg && msg.title);
    },
    beginExternalName(msg) {
      const caller = unknownVoicemailCaller(msg && msg.title);
      if (!caller) return;
      this.editingId = msg.id;
      this.editingDigits = caller.digits;
      this.editingPhone = caller.phone;
      this.editingName = '';
      this.nameError = '';
      this.$nextTick(() => {
        const input = Array.isArray(this.$refs.externalNameInput)
          ? this.$refs.externalNameInput[0]
          : this.$refs.externalNameInput;
        if (input && input.focus) input.focus();
      });
    },
    cancelExternalName() {
      this.editingId = null;
      this.editingName = '';
      this.nameError = '';
    },
    async saveExternalName() {
      const name = this.editingName.trim();
      const digits = this.editingDigits;
      if (!name || !digits || this.savingName) return;
      this.savingName = true;
      this.nameError = '';
      try {
        await axios.post('/api/sms/external-contact', {
          phoneDigits: digits,
          name,
          optIn: true,
          userAgent: 'Voice Messages',
        });
        this.inbox = applyExternalNameToVoiceInbox(this.inbox, digits, name);
        this.cancelExternalName();
      } catch (err) {
        this.nameError =
          (err.response && err.response.data && err.response.data.error) ||
          'Could not save name';
      } finally {
        this.savingName = false;
      }
    },
    openExternalEdit(msg) {
      const caller = externalVoicemailCaller(msg && msg.title);
      if (!caller) return;
      this.cancelExternalName();
      this.externalEdit = { fromPhoneDigits: caller.digits };
      this.externalEditPhone = caller.phone;
      this.externalEditName = caller.name;
      this.externalEditOptIn = false;
      this.externalEditError = '';
      this.externalEditReady = false;
      this.externalEditSaving = false;
      this.loadExternalEdit(caller.digits);
    },
    focusExternalEdit() {
      const el = this.$refs.externalEditPhoneInput;
      const input = Array.isArray(el) ? el[0] : el;
      if (input && input.focus) input.focus();
    },
    async loadExternalEdit(phoneDigits) {
      const loadId = (this._externalEditLoad || 0) + 1;
      this._externalEditLoad = loadId;
      try {
        const { data } = await axios.get('/api/sms/external-contact', {
          params: { phoneDigits },
        });
        if (this._externalEditLoad !== loadId || !this.externalEdit) return;
        this.externalEditPhone =
          formatSmsPhone(data && data.phoneDigits) || this.externalEditPhone;
        this.externalEditName =
          String((data && data.name) || '').trim() || this.externalEditName;
        this.externalEditOptIn = !!(data && data.optedIn);
        this.externalEditReady = true;
      } catch (err) {
        if (this._externalEditLoad !== loadId || !this.externalEdit) return;
        const reason =
          err.response && err.response.data && err.response.data.error;
        this.externalEditError = externalContactEditError(reason);
      } finally {
        if (this._externalEditLoad === loadId && this.externalEdit) {
          this.$nextTick(() => this.focusExternalEdit());
        }
      }
    },
    closeExternalEdit() {
      if (this.externalEditSaving) return;
      this._externalEditLoad = (this._externalEditLoad || 0) + 1;
      this.externalEdit = null;
      this.externalEditPhone = '';
      this.externalEditName = '';
      this.externalEditOptIn = false;
      this.externalEditError = '';
      this.externalEditReady = false;
    },
    async saveExternalEdit() {
      if (!this.externalEditCanSave) return;
      const fromPhoneDigits = this.externalEdit.fromPhoneDigits;
      const phoneDigits = this.externalEditPhone;
      const name = String(this.externalEditName || '').trim();
      const optIn = !!this.externalEditOptIn;
      this.externalEditSaving = true;
      this.externalEditError = '';
      try {
        const { data } = await axios.put('/api/sms/external-contact', {
          fromPhoneDigits,
          phoneDigits,
          name,
          optIn,
          userAgent: 'Voice Messages',
        });
        this.inbox = applyExternalEditToVoiceInbox(
          this.inbox,
          (data && data.fromPhoneDigits) || fromPhoneDigits,
          (data && data.phoneDigits) || phoneDigits,
          (data && data.name) || name,
        );
        this.externalEditSaving = false;
        this.closeExternalEdit();
      } catch (err) {
        const reason =
          err.response && err.response.data && err.response.data.error;
        this.externalEditError = externalContactEditError(reason);
      } finally {
        this.externalEditSaving = false;
      }
    },
    async refreshInbox() {
      await this.fetchInbox({ silent: this.inbox.length > 0 });
    },
    tryApplyPushHighlight() {
      const plan = pushHighlightPlan({
        query: this.$route && this.$route.query,
        inbox: this.inbox,
        now: new Date(this.clock),
      });
      if (!plan || !plan.found) return;
      if (this._appliedPushHighlightId === plan.id) return;
      this._appliedPushHighlightId = plan.id;
      this.clearListFilter();
      if (plan.expandGroup) this.collapsed[plan.expandGroup] = false;
      this.selectedId = plan.id;
      this.highlightId = plan.id;
      this.$nextTick(() => {
        const el =
          this.$el &&
          this.$el.querySelector(`[data-push-id="${plan.id}"]`);
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      });
      if (this._highlightTimer) clearTimeout(this._highlightTimer);
      this._highlightTimer = setTimeout(() => {
        if (this.highlightId === plan.id) this.highlightId = null;
      }, 3600);
    },
    async ensureUserRole() {
      if (this.userRole != null) return;
      try {
        const { data } = await axios.get('/api/user-info');
        const role = Number(data && data.role);
        this.userRole = Number.isFinite(role) ? role : 3;
      } catch {
        this.userRole = 3;
      }
    },
    async fetchInbox({ silent } = {}) {
      if (!silent && !this.inbox.length) {
        this.loading = true;
        this.loadError = '';
      }
      this.refreshing = true;
      try {
        await this.ensureUserRole();
        const { data } = await axios.get('/api/get-voice-memos');
        this.inbox = voiceMessagesForRole(
          Array.isArray(data) ? data : [],
          this.userRole,
        );
        this.loadError = '';
      } catch (err) {
        this.loadError = err.response?.data?.error || 'Failed to load voicemail.';
      } finally {
        this.loading = false;
        this.refreshing = false;
      }
    },
    toggleTranscript(msg) {
      this.selectedId = msg.id;
      this.transcriptId = this.transcriptId === msg.id ? null : msg.id;
    },
    async deleteMessage(msg) {
      if (!msg || !msg.id || this.deletingId) return;
      this.deletingId = msg.id;
      try {
        await axios.put(`/api/delete-voice-memo/${msg.id}`);
        if (this.playingId === msg.id) this.stopPlayback();
        if (this.transcriptId === msg.id) this.transcriptId = null;
        if (this.selectedId === msg.id) this.selectedId = null;
        this.inbox = dropVoiceMessageById(this.inbox, msg.id);
      } catch (err) {
        console.error('Failed to delete voicemail', err);
      } finally {
        this.deletingId = null;
      }
    },
    stopPlayback() {
      if (this.audioEl) {
        this.audioEl.pause();
        this.audioEl.removeAttribute('src');
      }
      if (this.objectUrl) {
        URL.revokeObjectURL(this.objectUrl);
        this.objectUrl = null;
      }
      this.playingId = null;
      this.playPaused = false;
      this.playCurrent = 0;
      this.playDuration = 0;
      this.playSeeking = false;
    },
    async blobForPlayback(data) {
      const raw = data instanceof Blob ? data : new Blob([data]);
      if (raw.size < 16) {
        throw new Error('No audio was saved for this voicemail.');
      }
      const head = new Uint8Array(await raw.slice(0, 16).arrayBuffer());
      if (head[0] === 0x7b || head[0] === 0x3c) {
        throw new Error('Server did not return audio.');
      }
      const mime = sniffAudioMimeFromBytes(head);
      return raw.slice(0, raw.size, mime);
    },
    async togglePlay(msg) {
      this.selectedId = msg.id;
      this.playError = '';
      if (this.playingId === msg.id && this.audioEl) {
        if (this.audioEl.paused) {
          await this.audioEl.play();
          this.playPaused = false;
        } else {
          this.audioEl.pause();
          this.playPaused = true;
        }
        return;
      }
      this.stopPlayback();
      const audio = this.bindAudioEl();
      if (!audio) return;
      try {
        const response = await axios.get(`/api/get-audioblob/${msg.id}`, {
          responseType: 'blob',
          params: voicePlaybackParams(navigator.userAgent),
        });
        const blob = await this.blobForPlayback(response.data);
        if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
        this.objectUrl = URL.createObjectURL(blob);
        audio.src = this.objectUrl;
        audio.load();
        this.playingId = msg.id;
        this.playPaused = false;
        this.playCurrent = 0;
        this.playDuration = 0;
        await audio.play();
        if (!msg.listenedOn) {
          await axios.put(`/api/mark-voicememo-listened/${msg.id}`);
          msg.listenedOn = new Date().toISOString();
        }
      } catch (err) {
        console.error('Failed to play voicemail', err);
        this.playError =
          (err && err.message) || 'Could not play this recording.';
        this.stopPlayback();
        this.selectedId = msg.id;
      }
    },
  },
};
</script>

<style scoped>
.voice-player {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}

.sms-page {
  width: 100%;
  max-width: 440px;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
  position: relative;
  font-family: Arial, 'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji',
    sans-serif;
}

.title-bar {
  flex-shrink: 0;
  background: #6e5875;
  color: #fff;
  font-size: 1.15em;
  font-weight: 600;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.title-buttons {
  display: flex;
  gap: 6px;
}

.title-button {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.55);
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}

.title-button:disabled {
  opacity: 0.6;
  cursor: default;
}

.sms-hint {
  flex-shrink: 0;
  padding: 6px 12px 0;
  font-size: 0.78em;
  color: #8a7a90;
}

.sms-list-filter {
  flex-shrink: 0;
  padding: 8px 12px 4px;
}

.sms-list-filter-wrap {
  position: relative;
  max-width: 88%;
}

.sms-list-filter-input {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #e0d6e4;
  border-radius: 6px;
  padding: 8px 32px 8px 10px;
  font-size: 0.92em;
}

.sms-list-filter-clear {
  position: absolute;
  right: 2px;
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  border: 0;
  background: transparent;
  color: #6a5570;
  cursor: pointer;
}

.sms-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 10px 12px 12px;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.sms-list::-webkit-scrollbar {
  display: none;
}

.sms-status {
  padding: 16px;
  color: #6a5570;
  font-size: 0.9em;
}

.sms-error {
  color: #a94442;
  margin-top: 8px;
}

.voice-group {
  margin-top: 14px;
  margin-bottom: 10px;
}

.voice-group:first-child {
  margin-top: 6px;
}

.voice-group-older {
  margin-top: 28px;
}

.voice-group-header {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #e0d6e4;
  background: #f3eef6;
  color: #4a3a52;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 0.85em;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 8px;
  text-align: left;
}

.voice-group-header i {
  width: 12px;
  font-size: 0.75em;
  color: #6a5570;
}

.voice-group-count {
  margin-left: auto;
  font-weight: 500;
  color: #8a7a90;
  font-size: 0.9em;
}

.sms-item {
  margin-bottom: 12px;
}

.sms-item-selected .sms-bubble {
  box-shadow: 0 0 0 2px #a080a8;
}

.sms-stack {
  max-width: 88%;
}

.voice-contact-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 40;
  padding: 16px;
}

.voice-contact-dialog {
  width: 100%;
  max-width: 320px;
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
}

.voice-contact-dialog p {
  margin: 0 0 12px;
  font-size: 0.92em;
  color: #333;
  line-height: 1.4;
}

.voice-name-input {
  display: block;
  width: 100%;
  box-sizing: border-box;
  margin: 0 0 12px;
  padding: 8px 10px;
  border: 1px solid #d4c8d8;
  border-radius: 6px;
  font-size: 0.92em;
  color: #333;
  background: #fff;
}

.voice-edit-field {
  display: block;
  margin: 0;
  font-size: 0.75em;
  color: #6a5570;
}

.voice-edit-optin {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 12px;
  font-size: 0.85em;
  color: #333;
}

.voice-contact-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.voice-name-save {
  border: 0;
  background: #a080a8;
  color: #fff;
  border-radius: 6px;
  padding: 4px 10px;
  cursor: pointer;
}

.voice-name-save:disabled {
  opacity: 0.6;
  cursor: default;
}

.voice-contact-cancel {
  border: 1px solid #c9b8d0;
  background: #fff;
  color: #4a3751;
  border-radius: 6px;
  padding: 4px 10px;
  cursor: pointer;
}

.voice-contact-cancel:disabled {
  opacity: 0.6;
  cursor: default;
}

.sms-item-top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}

.voice-from-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex: 1;
}

.sms-from {
  font-size: 0.72em;
  color: #1a73e8;
  min-width: 0;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sms-create-contact {
  flex-shrink: 0;
  margin: 0;
  padding: 0 6px;
  border: 1px solid #1a73e8;
  border-radius: 3px;
  background: transparent;
  color: #1a73e8;
  font-size: 0.65em;
  line-height: 1.4;
  font-weight: 500;
  cursor: pointer;
}

.sms-item-unlistened .sms-from {
  font-weight: 600;
}

.voice-kind {
  display: inline-block;
  margin-right: 6px;
  padding: 0 5px;
  border-radius: 4px;
  background: #e8dcec;
  color: #4a3a52;
  font-size: 0.92em;
  font-weight: 600;
}

.voice-kind-office {
  background: #c62828;
  color: #fff;
}

.sms-ago {
  flex-shrink: 0;
  font-size: 0.72em;
  color: #8a7a90;
}

.sms-bubble {
  position: relative;
  background: #f3eef6;
  border: 1px solid #e0d6e4;
  border-radius: 6px;
  padding: 8px 10px 10px;
  text-align: left;
}

.voice-actions {
  display: flex;
  gap: 8px;
}

.voice-scrub {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.voice-scrub-time {
  flex-shrink: 0;
  font-size: 0.72em;
  color: #6a5570;
  font-variant-numeric: tabular-nums;
  min-width: 2.2em;
}

.voice-scrub-bar {
  flex: 1;
  min-width: 0;
  height: 18px;
  margin: 0;
  accent-color: #a080a8;
  cursor: pointer;
}

.voice-action {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1px solid #c8b7ce;
  background: #fff;
  color: #4a3a52;
  border-radius: 6px;
  padding: 8px 6px;
  font-size: 0.82em;
  cursor: pointer;
}

.voice-action i {
  font-size: 0.95em;
}

.sms-body {
  font-size: 0.92em;
  color: #333;
  line-height: 1.35;
  white-space: pre-wrap;
  word-break: break-word;
  margin-top: 8px;
}

.sms-body-expanded {
  display: block;
  max-height: none;
}

@keyframes pixie-push-highlight {
  0%,
  100% {
    background: transparent;
  }
  25%,
  75% {
    background: #efe6f3;
  }
}

.sms-item-push-highlight {
  animation: pixie-push-highlight 1.2s ease-in-out 2;
  border-radius: 8px;
}

.sms-item-push-highlight .sms-bubble {
  box-shadow: 0 0 0 2px #6e5875;
}
</style>
